import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';

import {
  updateQuestionSetField as updateQuestionSetFieldAction,
  updateQuestionField as updateQuestionFieldAction,
  ensureRubricGuids,
  moveRubricQuestionSetBefore as moveRubricQuestionSetBeforeAction,
  moveRubricQuestionSetAfter as moveRubricQuestionSetAfterAction,
  moveRubricQuestionBefore as moveRubricQuestionBeforeAction,
  moveRubricQuestionAfter as moveRubricQuestionAfterAction,
  moveRubricQuestionToEnd as moveRubricQuestionToEndAction,
  moveRubricSourceBefore as moveRubricSourceBeforeAction,
  moveRubricSourceAfter as moveRubricSourceAfterAction,
  removeRubricSource as removeRubricSourceAction,
  createRubricQuestionSet as createRubricQuestionSetAction,
  addRubricQuestionFromSource as addRubricQuestionFromSourceAction,
  addRubricQuestionsFromSources as addRubricQuestionsFromSourcesAction,
  addRubricSourcesToQuestion as addRubricSourcesToQuestionAction,
  removeRubricQuestionSet as removeRubricQuestionSetAction,
  removeRubricQuestion as removeRubricQuestionAction,
  setRubricQuestionSetRetiredAt as setRubricQuestionSetRetiredAtAction,
  setRubricQuestionRetiredAt as setRubricQuestionRetiredAtAction,
  setRubricSourceRetiredAt as setRubricSourceRetiredAtAction,
} from './actions/rubric-editor-actions';

export const RubricEditorContext = createContext({
  rubric: null,
  errors: {
    questionSets: {},
    questions: {},
  },
  updateQuestionSetField: () => {},
  updateQuestionField: () => {},
  moveRubricQuestionSetBefore: () => {},
  moveRubricQuestionSetAfter: () => {},
  moveRubricQuestionBefore: () => {},
  moveRubricQuestionAfter: () => {},
  moveRubricQuestionToEnd: () => {},
  moveRubricSourceBefore: () => {},
  moveRubricSourceAfter: () => {},
  removeRubricSource: () => {},
  createRubricQuestionSet: () => {},
  addRubricQuestionFromSource: () => {},
  addRubricQuestionsFromSources: () => {},
  addRubricSourcesToQuestion: () => {},
  removeRubricQuestionSet: () => {},
  removeRubricQuestion: () => {},
  setRubricQuestionSetRetiredAt: () => {},
  setRubricQuestionRetiredAt: () => {},
  setRubricSourceRetiredAt: () => {},
});

function keyFingerprint(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

function computeRubricErrors(rubric) {
  const out = {
    questionSets: {},
    questions: {},
  };
  const questionSets = rubric?.document?.question_sets ?? [];

  const qsCounts = {};
  const qCounts = {};

  questionSets.forEach((qs) => {
    const setKey = keyFingerprint(qs.key);
    if (setKey) {
      qsCounts[setKey] = (qsCounts[setKey] ?? 0) + 1;
    }

    (qs.questions ?? []).forEach((q) => {
      const qKey = keyFingerprint(q.key);
      if (qKey) {
        qCounts[qKey] = (qCounts[qKey] ?? 0) + 1;
      }
    });
  });

  questionSets.forEach((qs) => {
    const setErrors = {};
    const key = keyFingerprint(qs.key);
    if (!key) {
      setErrors.key = 'A unique key is required';
    } else if (qsCounts[key] > 1) {
      setErrors.key = 'Question set key must be unique';
    }

    if (String(qs.name ?? '').trim() === '') {
      setErrors.name = 'Name is required';
    }

    if (Object.keys(setErrors).length > 0) {
      out.questionSets[qs.guid] = setErrors;
    }

    (qs.questions ?? []).forEach((q) => {
      const qErrors = {};
      const qKey = keyFingerprint(q.key);
      if (!qKey) {
        qErrors.key = 'A unique key is required';
      } else if (qCounts[qKey] > 1) {
        qErrors.key = 'Question key must be unique';
      }

      if (String(q.text ?? '').trim() === '') {
        qErrors.text = 'Question text is required';
      }

      if (Object.keys(qErrors).length > 0) {
        out.questions[q.guid] = qErrors;
      }
    });
  });

  return out;
}

function wrapRubricActions(setRubric, handlers) {
  return Object.fromEntries(
    Object.entries(handlers).map(([key, action]) => [
      key,
      (...args) => setRubric((rubric) => action(rubric, ...args)),
    ])
  );
}

/**
 * Holds a working copy of the rubric and action functions that update it
 * (mirrors capabilities-assessment EditorContext: default context, imported actions, wrap*).
 */
export function RubricProvider({ rubric: rubricProp, children }) {
  const [rubric, setRubric] = useState(() =>
    ensureRubricGuids(cloneDeep(rubricProp ?? null))
  );

  const rubricSyncKey = useMemo(() => {
    if (!rubricProp?.id) {
      return null;
    }
    const u = rubricProp.updated_at;
    const updatedTick =
      u == null ? '' : u instanceof Date ? u.getTime() : String(u);
    return `${rubricProp.id}:${updatedTick}`;
  }, [rubricProp?.id, rubricProp?.updated_at]);

  useEffect(() => {
    if (!rubricProp) {
      setRubric(null);
      return;
    }
    setRubric(ensureRubricGuids(cloneDeep(rubricProp)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rubricSyncKey tracks id/updated_at; `rubricProp` reference churns from denormalize
  }, [rubricSyncKey]);

  const actionsContext = useMemo(
    () =>
      wrapRubricActions(setRubric, {
        updateQuestionSetField: updateQuestionSetFieldAction,
        updateQuestionField: updateQuestionFieldAction,
        moveRubricQuestionSetBefore: moveRubricQuestionSetBeforeAction,
        moveRubricQuestionSetAfter: moveRubricQuestionSetAfterAction,
        moveRubricQuestionBefore: moveRubricQuestionBeforeAction,
        moveRubricQuestionAfter: moveRubricQuestionAfterAction,
        moveRubricQuestionToEnd: moveRubricQuestionToEndAction,
        moveRubricSourceBefore: moveRubricSourceBeforeAction,
        moveRubricSourceAfter: moveRubricSourceAfterAction,
        removeRubricSource: removeRubricSourceAction,
        createRubricQuestionSet: createRubricQuestionSetAction,
        addRubricQuestionFromSource: addRubricQuestionFromSourceAction,
        addRubricQuestionsFromSources: addRubricQuestionsFromSourcesAction,
        addRubricSourcesToQuestion: addRubricSourcesToQuestionAction,
        removeRubricQuestionSet: removeRubricQuestionSetAction,
        removeRubricQuestion: removeRubricQuestionAction,
        setRubricQuestionSetRetiredAt: setRubricQuestionSetRetiredAtAction,
        setRubricQuestionRetiredAt: setRubricQuestionRetiredAtAction,
        setRubricSourceRetiredAt: setRubricSourceRetiredAtAction,
      }),
    []
  );

  const errors = useMemo(() => computeRubricErrors(rubric), [rubric]);

  const context = useMemo(
    () => ({
      rubric,
      errors,
      ...actionsContext,
    }),
    [rubric, errors, actionsContext]
  );

  return (
    <RubricEditorContext.Provider value={context}>
      {children}
    </RubricEditorContext.Provider>
  );
}

export function useRubricContext() {
  return useContext(RubricEditorContext);
}
